/**
 * TeamAnalyticsPage — Team productivity dashboard.
 * Shows approvals per user, average review time, daily throughput, and bottleneck indicators.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Clock, AlertTriangle, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamAnalyticsPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/team?days=${days}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Laedt…</div>;
  if (!data) return null;

  const maxDaily = Math.max(1, ...data.daily_approvals.map((d) => d.approvals));

  return (
    <div data-testid="team-analytics-page" className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">Team-Produktivitaet</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">
            Approvals, Durchlaufzeiten und Bottlenecks der letzten {data.period_days} Tage
          </p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32" data-testid="analytics-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 Tage</SelectItem>
            <SelectItem value="30">30 Tage</SelectItem>
            <SelectItem value="90">90 Tage</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-4">
        <Kpi label="Bearbeitet" value={data.totals.content_touched} icon={<TrendingUp className="w-5 h-5 text-[#2563EB]" />} />
        <Kpi label="Freigegeben" value={data.totals.approved} icon={<Users className="w-5 h-5 text-green-600" />} />
        <Kpi
          label="AVG Review"
          value={`${data.avg_review_hours} h`}
          sub={`p95: ${data.p95_review_hours} h`}
          icon={<Clock className="w-5 h-5 text-amber-600" />}
        />
        <Kpi
          label="In Review > 24h"
          value={data.stuck_in_review.length}
          icon={<AlertTriangle className={`w-5 h-5 ${data.stuck_in_review.length ? "text-red-600" : "text-gray-400"}`} />}
        />
      </div>

      {/* Daily approvals chart */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader><CardTitle className="font-heading text-base">Approvals pro Tag</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-32" data-testid="daily-chart">
            {data.daily_approvals.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.approvals}`}
                className="flex-1 bg-[#2563EB]/20 hover:bg-[#2563EB] transition-all rounded-t"
                style={{ height: `${Math.max(2, (d.approvals / maxDaily) * 100)}%` }}
              />
            ))}
          </div>
          <div className="text-[10px] text-[var(--studio-text-muted)] flex justify-between mt-1">
            <span>{data.daily_approvals[0]?.date}</span>
            <span>{data.daily_approvals[data.daily_approvals.length - 1]?.date}</span>
          </div>
        </CardContent>
      </Card>

      {/* Top performers */}
      <div className="grid grid-cols-3 gap-4">
        <RankCard title="Top Reviewer (Approved)" items={data.top_approvers} emptyText="Noch keine Freigaben" />
        <RankCard title="Top Autoren (Content erstellt)" items={data.top_creators} emptyText="Noch keine Autoren" />
        <RankCard title="Top Ablehner" items={data.top_rejectors} emptyText="Keine Ablehnungen" />
      </div>

      {/* Bottleneck list - always renders for testability */}
      <Card className={`shadow-none ${data.stuck_in_review.length > 0 ? "border-red-200 bg-red-50/30" : "border-[var(--studio-border)]"}`}>
        <CardHeader>
          <CardTitle className={`font-heading text-base flex items-center gap-2 ${data.stuck_in_review.length > 0 ? "text-red-700" : ""}`}>
            <AlertTriangle className="w-4 h-4" /> {data.stuck_in_review.length > 0 ? "Blocker: seit >24h in Review" : "Keine Blocker"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5" data-testid="stuck-list">
          {data.stuck_in_review.length === 0 ? (
            <p className="text-xs text-[var(--studio-text-muted)]" data-testid="stuck-empty">
              Alle Inhalte werden innerhalb von 24h bearbeitet.
            </p>
          ) : (
            data.stuck_in_review.slice(0, 10).map((s) => (
              <div key={s.content_id} className="flex items-center justify-between text-xs bg-white p-2 rounded">
                <code className="text-[11px]">{s.content_id.slice(0, 18)}</code>
                <Badge variant="secondary" className="text-red-700 bg-red-100">{s.age_hours}h</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, icon }) {
  return (
    <Card className="border-[var(--studio-border)] shadow-none">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-[var(--studio-text-muted)] uppercase tracking-wider">{label}</p>
          {icon}
        </div>
        <p className="text-3xl font-heading font-bold mt-2">{value}</p>
        {sub && <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function RankCard({ title, items, emptyText }) {
  return (
    <Card className="border-[var(--studio-border)] shadow-none">
      <CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {items.length === 0 ? (
          <p className="text-xs text-[var(--studio-text-muted)]">{emptyText}</p>
        ) : (
          items.map((u, i) => (
            <div key={u.user_id} className="flex items-center justify-between text-sm">
              <span className="truncate">
                <span className="text-[var(--studio-text-muted)] font-mono text-xs mr-2">#{i + 1}</span>
                {u.label}
              </span>
              <Badge variant="secondary">{u.count}</Badge>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
